/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   AppTooltip.tsx
 *     Description     :   Unified tooltip — the ONE tooltip for the portal.
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-06-15 | Peter Dobler | Re-based off MUI onto a pure
 *                         token-driven CSS tooltip (Spec 197 — MUI retirement).
 *                         2026-06-15 | Auto-close after 3 s so tooltips never block
 *                         the next screen or modal; JS-controlled visibility replaces
 *                         the pure-CSS group-hover approach.
 *                         2026-06-16 | Render the bubble through a portal with fixed
 *                         positioning so it is never clipped by a table/scroll
 *                         container's overflow (tooltips were "cut off in the row").
 *     Constitution    :   v1.21.0
 *     Feature         :   197-portal-light-theme-responsive
 * =========================================================================================
 *
 *  Token-driven tooltip with a 5-second auto-close.
 *  - enterDelay ms after mouseenter/focus → tooltip opens
 *  - 5 000 ms after opening → tooltip auto-closes
 *  - mouseleave/blur → tooltip closes immediately
 *
 *  The bubble is rendered in a portal on document.body and positioned `fixed` from the
 *  anchor's bounding rect, so an ancestor with `overflow: hidden/auto` (every DataTable /
 *  DualScrollTable scroll container) can no longer clip it. Closes on scroll/resize so the
 *  floating bubble can't drift away from its anchor.
 *
 *  Same public API as before — no call-site changes needed.
 *
 *  @module ui
 *  @spec 197-portal-light-theme-responsive
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface AnchorRect { top: number; left: number; right: number; bottom: number; width: number; height: number; }

export interface AppTooltipProps {
  /** Tooltip content — string or rich JSX */
  title: React.ReactNode;
  /** Element to wrap */
  children: React.ReactElement;
  /** Placement relative to the anchor (default: 'top'). MUI-style compound
   *  placements like 'top-start' are accepted and reduced to their side. */
  placement?: string;
  /** Maximum width in pixels (default: 280) */
  maxWidth?: number;
  /** Delay before opening in ms (default: 400) */
  enterDelay?: number;
  /** Auto-close delay in ms after the tooltip opens (default: 5000) */
  closeDelay?: number;
  /** Allow interacting with the tooltip (links, copy) — keeps it open on hover */
  interactive?: boolean;
  /** Disable the tooltip entirely */
  disabled?: boolean;
  /** Arrow pointer (default: true) */
  arrow?: boolean;
  /** Wrapper display/layout classes. Defaults to `inline-flex`. Pass `block`
   *  when wrapping a block/grid/flex child to avoid width collapse. */
  className?: string;
}

const sideOf = (placement: string): TooltipPlacement => {
  const side = placement.split('-')[0];
  return (['top', 'bottom', 'left', 'right'].includes(side) ? side : 'top') as TooltipPlacement;
};

/** Gap between the anchor and the bubble (matches the old mb-2/mt-2/ml-2/mr-2 = 0.5rem). */
const GAP = 8;

/** Fixed-position style for the bubble, computed from the anchor rect + side. */
const bubbleStyleFor = (side: TooltipPlacement, r: AnchorRect, maxWidth: number): React.CSSProperties => {
  const base: React.CSSProperties = { position: 'fixed', maxWidth, zIndex: 1070 };
  switch (side) {
    case 'top':
      return { ...base, top: r.top - GAP, left: r.left + r.width / 2, transform: 'translate(-50%, -100%)' };
    case 'bottom':
      return { ...base, top: r.bottom + GAP, left: r.left + r.width / 2, transform: 'translate(-50%, 0)' };
    case 'left':
      return { ...base, top: r.top + r.height / 2, left: r.left - GAP, transform: 'translate(-100%, -50%)' };
    case 'right':
      return { ...base, top: r.top + r.height / 2, left: r.right + GAP, transform: 'translate(0, -50%)' };
  }
};

const arrowPos: Record<TooltipPlacement, string> = {
  top:    'bottom-[-3px] left-1/2 -translate-x-1/2',
  bottom: 'top-[-3px] left-1/2 -translate-x-1/2',
  left:   'right-[-3px] top-1/2 -translate-y-1/2',
  right:  'left-[-3px] top-1/2 -translate-y-1/2',
};

/**
 * AppTooltip — the canonical, token-driven tooltip.
 * Auto-closes 5 seconds after opening so it never blocks the next screen or modal.
 */
export const AppTooltip: React.FC<AppTooltipProps> = ({
  title,
  children,
  placement = 'top',
  maxWidth = 280,
  enterDelay = 400,
  closeDelay = 5000,
  interactive = false,
  disabled = false,
  arrow = true,
  className,
}) => {
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const anchorRef  = useRef<HTMLSpanElement>(null);
  const openTimer  = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const graceTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearTimers = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    clearTimeout(graceTimer.current);
  };

  // On unmount, cancel a pending open, close or grace timer: one left running fires setState on a component that is
  // gone (in a test run, after the environment is torn down: "window is not defined").
  useEffect(() => clearTimers, []);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
  }, []);

  const open = useCallback(() => {
    clearTimers();
    openTimer.current = setTimeout(() => {
      measure();
      setVisible(true);
      closeTimer.current = setTimeout(() => setVisible(false), closeDelay);
    }, enterDelay);
  }, [enterDelay, closeDelay, measure]);

  const close = useCallback(() => {
    clearTimers();
    setVisible(false);
  }, []);

  // Interactive tooltips: allow the cursor to travel from anchor to bubble
  // without closing. Non-interactive ones close immediately on leave.
  const closeFromAnchor = useCallback(() => {
    if (!interactive) { close(); return; }
    clearTimeout(graceTimer.current);
    graceTimer.current = setTimeout(() => setVisible(false), 120);
  }, [interactive, close]);

  // A floating fixed bubble would drift if the page scrolls/resizes — close it.
  useEffect(() => {
    if (!visible) return;
    const onMove = () => close();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [visible, close]);

  if (disabled || !title) return children;

  const side = sideOf(placement);
  const surface = 'bg-[var(--color-text-primary)] text-[var(--color-bg-card)]';

  return (
    <span
      ref={anchorRef}
      className={className || 'inline-flex'}
      onMouseEnter={open}
      onMouseLeave={closeFromAnchor}
      onFocus={open}
      onBlur={close}
    >
      {children}
      {visible && rect && createPortal(
        <span
          role="tooltip"
          style={bubbleStyleFor(side, rect, maxWidth)}
          onMouseEnter={interactive ? clearTimers : undefined}
          onMouseLeave={interactive ? close : undefined}
          className={clsx(
            'w-max rounded-[var(--radius-md)] px-3 py-2',
            'text-[0.8125rem] leading-snug shadow-[var(--shadow-md)] whitespace-normal',
            surface,
            'transition-opacity duration-150 opacity-100',
            interactive ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          {title}
          {arrow && (
            <span
              aria-hidden="true"
              className={clsx('absolute h-1.5 w-1.5 rotate-45', surface, arrowPos[side])}
            />
          )}
        </span>,
        document.body,
      )}
    </span>
  );
};

export default AppTooltip;
